import { useCallback, useState } from 'react';
import * as Yup from 'yup';
// form
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
// @mui
import { LoadingButton } from '@mui/lab';
import {
  Alert,
  Box,
  Button,
  Card,
  CircularProgress,
  Grid,
  Stack,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import { uploadApi } from 'src/api/upload';
import i18n from 'src/locales/i18n';
import useImageCompression from 'src/hooks/useImageCompression';
import {
  PHONE_REGEX,
  PHONE_VALIDATION_I18N_KEY,
} from 'src/utils/phoneValidation';
import { GetUserDto } from 'woofwoof-api';
import FormProvider, {
  RHFTextField,
  RHFPhoneField,
  RHFUploadAvatar,
} from '../../../../components/hook-form';
import { useSnackbar } from '../../../../components/snackbar';
import AccountBusinessConfirmationModal from './AccountBusinessConfirmationModal';
import { translateKey } from 'src/locales/translate';


// ----------------------------------------------------------------------

export type AccountBusinessFormValuesProps = {
  logoUrl?: File | string | null;
  name: string;
  nip: string;
  street: string;
  city: string;
  zip: string;
  phoneNumber?: string;
  email?: string;
  website?: string;
  specializations?: string[];
  description?: string;
};

type AccountBusinessProps = {
  onSubmit?: (values: AccountBusinessFormValuesProps) => void;
  onBack?: (values: AccountBusinessFormValuesProps) => void;
  stepValues?: AccountBusinessFormValuesProps | GetUserDto | undefined;
  isLoading?: boolean;
};

export default function AccountBusiness({
  onSubmit,
  stepValues,
  onBack,
  isLoading,
}: AccountBusinessProps) {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);

  const openConfirmationModal = () => {
    setIsConfirmationModalOpen(true);
  };

  const closeConfirmationModal = () => {
    setIsConfirmationModalOpen(false);
  };

  const AccountBusinessSchema = Yup.object().shape({
    logoUrl: Yup.mixed().nullable().required('Logo jest wymagane'),
    name: Yup.string().required(translateKey("hardcoded.nazwa_placowki_profilu_jest_wymagana")),
    nip: Yup.string(),
    street: Yup.string().required('Adres jest wymagany'),
    city: Yup.string().required(translateKey("validation.cityRequired")),
    zip: Yup.string()
      .matches(/^\d{2}-\d{3}$/, {
        message: 'Wymagany format to XX-XXX',
      })
      .required(translateKey("validation.postalCodeRequired")),
    phoneNumber: Yup.string()
      .required('Numer telefonu jest wymagany')
      .matches(PHONE_REGEX, () => i18n.t(PHONE_VALIDATION_I18N_KEY)),
    email: Yup.string()
      .email(translateKey("hardcoded.nieprawidlowy_adres_email"))
      .required('Adres email jest wymagany'),
    website: Yup.string()
      .optional()
      .transform((value) => {
        if (!value || value === '') return undefined;
        // Add protocol if missing
        if (!/^https?:\/\//i.test(value)) {
          return `https://${value}`;
        }
        return value;
      })
      .test('is-url', translateKey("hardcoded.nieprawidlowy_adres_url"), (value) => {
        if (!value) return true; // optional field
        try {
          new URL(value);
          return true;
        } catch {
          return false;
        }
      }),
    specializations: Yup.array().of(Yup.string()),
    description: Yup.string(),
  });

  const { compressImageFile, imageLoading } = useImageCompression();

  const defaultValues = {
    logoUrl: (stepValues as any)?.logoUrl || null,
    name: (stepValues as any)?.name || '',
    nip: (stepValues as any)?.nip || '',
    city: (stepValues as any)?.city || '',
    street: (stepValues as any)?.street || '',
    zip: (stepValues as any)?.zip || '',
    phoneNumber: (stepValues as any)?.phoneNumber || '',
    email: (stepValues as any)?.email || '',
    website: (stepValues as any)?.website || '',
    specializations:
      (stepValues as any)?.specializations?.map((spec: any) =>
        spec.toString(),
      ) || [],
    description: (stepValues as any)?.description || '',
  };

  const methods = useForm<AccountBusinessFormValuesProps>({
    resolver: yupResolver(AccountBusinessSchema),
    defaultValues,
  });

  const { getValues, handleSubmit, setValue } = methods;

  const handleDrop = useCallback(
    async (acceptedFiles: File[]) => {
      const file = acceptedFiles[0];
      if (!file) return;

      try {
        const compressedFile = await compressImageFile([file]);
        if (!compressedFile) return;

        const response = await uploadApi.uploadControllerUploadPublic({
          file: compressedFile as File,
        } as any);

        const url = response.data.url;
        setValue('logoUrl', url, { shouldValidate: true });
      } catch (error) {
        enqueueSnackbar(translateKey("hardcoded.blad_podczas_przesylania_logo"), {
          variant: 'error',
        });
      }
    },
    [compressImageFile, setValue, enqueueSnackbar],
  );

  const handleOnSubmit = async (formValues: AccountBusinessFormValuesProps) => {
    try {
      await onSubmit?.(formValues);

      closeConfirmationModal();
    } catch (error) {}
  };

  return (
    <FormProvider methods={methods} onSubmit={handleSubmit(handleOnSubmit)}>
      <Grid
        container
        spacing={3}
        sx={{ px: { xs: 2, sm: 2, md: 2, lg: 0, xl: 0 } }}
      >
        <Grid item xs={12} md={12} lg={4} xl={4}>
          <Card
            sx={{
              boxShadow: theme.customShadows.z24,
              px: 3,
              py: 3,
            }}
          >
            <Typography variant="h6" sx={{ mb: 2 }}>{translateKey("hardcoded.logo_placowki")}</Typography>
            <Box
              sx={{
                display: 'flex',
                flex: 1,
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'center',
                py: 7,
              }}
            >
              {imageLoading ? (
                <Stack
                  sx={{
                    height: 1,
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                >
                  <Box
                    sx={{
                      zIndex: 9999,
                      display: 'flex',
                      borderRadius: '50%',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      height: 144,
                      width: 144,
                      border: `1px dashed ${alpha(
                        theme.palette.grey[500],
                        0.32,
                      )}`,
                    }}
                  >
                    <Box
                      sx={{
                        zIndex: 7,
                        display: 'flex',
                        borderRadius: '50%',
                        position: 'absolute',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 128,
                        height: 128,
                        color: theme.palette.text.disabled,
                        backgroundColor: theme.palette.primary.lighter,
                        transition: theme.transitions.create('opacity', {
                          easing: theme.transitions.easing.easeInOut,
                          duration: theme.transitions.duration.shorter,
                        }),
                      }}
                    >
                      <CircularProgress />
                    </Box>
                  </Box>
                  <Typography
                    variant="caption"
                    sx={{
                      mt: 2,
                      mx: 'auto',
                      display: 'block',
                      textAlign: 'center',
                      color: 'text.secondary',
                    }}
                  >
                    Dozwolone formaty *.jpeg, *.jpg, *.png
                  </Typography>
                </Stack>
              ) : (
                <RHFUploadAvatar
                  name="logoUrl"
                  maxSize={3145728}
                  onDrop={handleDrop}
                  disabled={isLoading}
                  helperText={
                    <Typography
                      variant="caption"
                      sx={{
                        mt: 2,
                        mx: 'auto',
                        display: 'block',
                        textAlign: 'center',
                        color: 'text.secondary',
                      }}
                    >
                      Dozwolone formaty *.jpeg, *.jpg, *.png
                    </Typography>
                  }
                />
              )}
            </Box>
          </Card>
        </Grid>

        <Grid item xs={12} md={12} lg={8} xl={8}>
          <Card sx={{ p: 3, boxShadow: theme.customShadows.z24 }}>
            <Typography variant="h6" sx={{ mb: 3 }}>{translateKey("hardcoded.informacje_o_placowce")}</Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <Box
                rowGap={3}
                columnGap={2}
                display="grid"
                gridTemplateColumns={{
                  xs: 'repeat(1, 1fr)',
                  sm: 'repeat(1, 1fr)',
                  md: 'repeat(3, 1fr)',
                }}
              >
                <Box
                  sx={{
                    gridColumn: { xs: 'span 1', sm: 'span 1', md: 'span 2' },
                  }}
                >
                  <RHFTextField
                    name="name"
                    label={translateKey("hardcoded.nazwa_placowki_profilu")}
                    disabled={isLoading}
                    required
                  />
                </Box>

                <Box
                  sx={{
                    gridColumn: { xs: 'span 1', sm: 'span 1', md: 'span 1' },
                  }}
                >
                  <RHFTextField name="nip" label="NIP" disabled={isLoading} />
                </Box>
              </Box>

              {/* Address, City, Zip in one row */}
              <Box
                rowGap={3}
                columnGap={2}
                display="grid"
                gridTemplateColumns={{
                  xs: 'repeat(2, 1fr)',
                  lg: 'repeat(3, 1fr)',
                }}
              >
                <Box
                  sx={{
                    gridColumn: {
                      xs: 'span 2',
                      lg: 'span 1',
                    },
                  }}
                >
                  <RHFTextField
                    name="street"
                    label={translateKey("search.book.cardAddress")}
                    disabled={isLoading}
                    required
                  />
                </Box>

                <Box
                  sx={{
                    gridColumn: {
                      xs: 'span 1',
                      lg: 'span 1',
                    },
                  }}
                >
                  <RHFTextField
                    name="city"
                    label="Miasto"
                    disabled={isLoading}
                    required
                  />
                </Box>

                <Box
                  sx={{
                    gridColumn: {
                      xs: 'span 1',
                      lg: 'span 1',
                    },
                  }}
                >
                  <RHFTextField
                    name="zip"
                    label={translateKey("search.book.zipCode")}
                    disabled={isLoading}
                    required
                  />
                </Box>
              </Box>

              {/* Phone, Email and Website in rows */}
              <Box
                rowGap={3}
                columnGap={2}
                display="grid"
                gridTemplateColumns={{
                  xs: 'repeat(1, 1fr)',
                  sm: 'repeat(1, 1fr)',
                  md: 'repeat(2, 1fr)',
                }}
              >
                <RHFPhoneField
                  name="phoneNumber"
                  required
                  label={translateKey("auth.fieldPhone")}
                  disabled={isLoading}
                />

                <RHFTextField
                  name="email"
                  required
                  label="Email kontaktowy"
                  disabled={isLoading}
                  placeholder="kontakt@klinika.pl"
                />
              </Box>
            </Box>

            <Alert severity="info" sx={{ mt: 3 }}>{translateKey("hardcoded.logo_placowki_jest_wymagane_to_kluczowy_element_twojej")}</Alert>
            <Stack
              flexDirection="row"
              justifyContent={onBack ? 'space-between' : 'flex-end'}
              sx={{ mt: 3 }}
            >
              {onBack && (
                <Button
                  onClick={() => onBack(getValues())}
                  color="inherit"
                  variant="outlined"
                  disabled={isLoading}
                >{translateKey("search.book.backToBooking")}</Button>
              )}
              <LoadingButton
                variant="contained"
                disabled={isLoading}
                onClick={handleSubmit(() => openConfirmationModal())}
              >
                <span>{translateKey("common.save")}</span>
              </LoadingButton>
            </Stack>
          </Card>
        </Grid>
      </Grid>
      <AccountBusinessConfirmationModal
        isOpen={isConfirmationModalOpen}
        onCancel={closeConfirmationModal}
        onConfirm={handleSubmit(handleOnSubmit)}
        isLoading={isLoading}
      />
    </FormProvider>
  );
}
